
import { computed, defineComponent, onBeforeUnmount, PropType, reactive, toRaw } from 'vue'
import ymaps from 'yandex-maps'

// components
import TheMap from '@/common/components/UI/TheMap/index.vue'
import MapGeocoder from '@/common/components/UI/MapGeocoder/index.vue'

// types
import { CityWithCoordinates } from '@/core/types/cities.types'

// constants
import { YANDEX_MAP_TYPE, TWO_GIS_MAP_TYPE } from '@/core/constants/map.constants'

// realisations
import { geocoder } from '@/core/realisations/geocoder.realisation'

// composable
import useGeocoder from '@/common/composable/useGeocoder'
import useMapGraphics from '@/common/composable/useMapGraphics'

export default defineComponent({
    components: {
        'the-map': TheMap,
        'map-geocoder': MapGeocoder,
    },
    props: {
        city: {
            type: Object as PropType<CityWithCoordinates>,
            required: true,
        },
        latitude: {
            type: Number,
            default: null,
        },
        longitude: {
            type: Number,
            default: null,
        },
        fullAddress: {
            type: String,
            default: null,
        },
    },
    emits: ['set-address'],
    setup(props, { emit }) {
        const state = reactive({
            instance: undefined as ymaps.Map | undefined,
            map_type: YANDEX_MAP_TYPE,
            //
            is_city_loading: false,
            is_city_failed: false,
            is_city_setted: false,
            //
            is_geocoding: false,
            is_geocoding_failed: false,
            geocoded_address: null as string | null,
            geocoded_street: null as string | null,
            geocoded_latitude: null as number | null,
            geocoded_longitude: null as number | null,
            //
            map_events: null as ymaps.IEventGroup | null,
        })

        const getInstance = (): ymaps.Map | undefined => {
            return toRaw(state.instance)
        }

        const isYandex = computed(() => state.map_type === YANDEX_MAP_TYPE)
        const isTwoGis = computed(() => state.map_type === TWO_GIS_MAP_TYPE)

        const { getCoordsFromResponse } = useGeocoder()
        const { createPoint } = useMapGraphics()

        const geocodeQuery = async (geocode_query: string): Promise<number[] | null | void> => {
            try {
                state.is_city_loading = true

                if (isYandex.value) {
                    const response = await geocoder.yandexGeocode(geocode_query, null)
                    const coordinates = getCoordsFromResponse(response)

                    return coordinates
                }

                if (isTwoGis.value) {
                    const response = await geocoder.twoGisGeocodeByAddress(geocode_query, null)
                    const coordinates = getCoordsFromResponse(response)

                    return coordinates
                }

                state.is_city_failed = false
            } catch (error) {
                console.error(error)
                state.is_city_failed = true
            } finally {
                state.is_city_loading = false
            }
        }

        const onMapLayerChange = (layer: string) => {
            state.map_type = layer
        }

        const geocodeCoordinates = async (coordinates: number[]) => {
            const map = getInstance()
            if (!map) return

            const placemark = map.geoObjects.get(0)
            if (!placemark) return

            try {
                state.is_geocoding = true
                // @ts-ignore
                placemark.properties.set('iconContent', 'Геокодирование адреса...')

                if (isYandex.value) {
                    const yandex_coords = [...coordinates].reverse().join(',')

                    const response = await geocoder.yandexGeocode(yandex_coords, null)
                    const feature_members = response.response.GeoObjectCollection.featureMember

                    if (!feature_members.length) {
                        // @ts-ignore
                        placemark.properties.set('iconContent', 'Адрес не опознан')
                        state.geocoded_address = null
                        state.geocoded_street = null
                        state.is_geocoding = false
                        state.is_geocoding_failed = false

                        return
                    }

                    const new_address = `${feature_members[0].GeoObject.description}, ${feature_members[0].GeoObject.name}`
                    state.geocoded_address = new_address

                    const address_components =
                        feature_members[0].GeoObject.metaDataProperty.GeocoderMetaData.Address
                            .Components
                    const object_address_components: Record<string, string> = {}

                    address_components.forEach((component) => {
                        object_address_components[component.kind] = component.name
                    })

                    state.geocoded_street = object_address_components.street || null
                }

                if (isTwoGis.value) {
                    const response = await geocoder.twoGisGeocodeByCoords(coordinates)

                    if (!response.result) {
                        // @ts-ignore
                        placemark.properties.set('iconContent', 'Адрес не опознан')
                        state.geocoded_address = null
                        state.geocoded_street = null
                        state.is_geocoding = false
                        state.is_geocoding_failed = false

                        return
                    }

                    const new_address = response.result.items[0].full_address_name
                        ? response.result.items[0].full_address_name
                        : response.result.items[0].full_name

                    if (response.result.items[0].address_name) {
                        const parts = response.result.items[0].address_name.split(',')
                        state.geocoded_street = parts[0]
                    }

                    state.geocoded_address = new_address
                }

                // @ts-ignore
                placemark.properties.set('iconContent', state.geocoded_address)
                state.geocoded_latitude = coordinates[0]
                state.geocoded_longitude = coordinates[1]

                state.is_geocoding_failed = false

                emit('set-address', {
                    address: state.geocoded_address,
                    street: state.geocoded_street,
                    latitude: state.geocoded_latitude,
                    longitude: state.geocoded_longitude,
                })
            } catch (error) {
                console.error(error)

                // @ts-ignore
                placemark.properties.set('iconContent', 'Ошибка геокодирования')

                // setting_address_state.new_address = null
                state.is_geocoding_failed = true
            } finally {
                state.is_geocoding = false
            }
        }

        const setAddressByDrag = () => {
            const map = getInstance()
            if (!map) return

            const placemark = map.geoObjects.get(0)
            if (!placemark) return

            // @ts-ignore
            const coordinates = placemark.geometry.getCoordinates()
            const latitude = coordinates[0]
            const longitude = coordinates[1]

            geocodeCoordinates([latitude, longitude])
        }

        const setPlacemark = (coordinates: number[], iconContent?: string) => {
            const map = getInstance()
            if (!map) return

            map.geoObjects.removeAll()

            const placemark = createPoint(
                coordinates,
                {
                    iconContent,
                },
                {
                    preset: 'islands#blueStretchyIcon',
                    draggable: false,
                }
            )

            map.geoObjects.add(placemark)
        }

        const onMapClick = (
            event: MouseEvent | ymaps.IEvent<Record<any, any>, Record<any, any>>
        ) => {
            // @ts-ignore
            const coordinates = event.get('coords')
            const latitude = coordinates[0]
            const longitude = coordinates[1]

            setPlacemark(coordinates)
            geocodeCoordinates([latitude, longitude])
        }

        const setCityCenter = async () => {
            const map = getInstance()

            if (!map) {
                return
            }

            if (props.city.latitude && props.city.longitude) {
                const coordinates = [props.city.latitude, props.city.longitude]

                map.setCenter(coordinates)
                state.is_city_setted = true

                if (props.city.coordinates) {
                    map.options.set('restrictMapArea', props.city.coordinates)
                }

                return
            }

            const coordinates = await geocodeQuery(props.city.name)

            if (!coordinates) {
                return
            }

            map.setCenter(coordinates)
            state.is_city_setted = true
        }

        const onMapReady = async (map: ymaps.Map) => {
            state.instance = map
            await setCityCenter()

            if (props.latitude && props.longitude) {
                state.geocoded_address = props.fullAddress
                state.geocoded_latitude = props.latitude
                state.geocoded_longitude = props.longitude

                const map = getInstance()
                if (!map) return

                map.setCenter([props.latitude, props.longitude])
                setPlacemark([props.latitude, props.longitude])

                const placemark = map.geoObjects.get(0)
                if (!placemark) return

                // @ts-ignore
                placemark.properties.set('iconContent', props.fullAddress)

                return
            }

            state.map_events = map.events.group().add('click', onMapClick)
        }

        const onSelectAddress = (option: {
            name: string
            street: string | null
            description?: string
            coordinates: number[]
        }) => {
            state.geocoded_address = option.name
            state.geocoded_street = option.street
            state.geocoded_latitude = option.coordinates[0]
            state.geocoded_longitude = option.coordinates[1]

            const map = getInstance()
            if (!map) return

            map.setCenter(option.coordinates)

            const placemark = map.geoObjects.get(0)
            if (placemark) {
                //@ts-ignore
                placemark.geometry.setCoordinates(option.coordinates)

                // @ts-ignore
                placemark.properties.set('iconContent', option.name)
            } else {
                setPlacemark(option.coordinates, option.name)
            }

            emit('set-address', {
                address: state.geocoded_address,
                street: state.geocoded_street,
                latitude: state.geocoded_latitude,
                longitude: state.geocoded_longitude,
            })
        }

        onBeforeUnmount(() => {
            state.map_events?.removeAll()
        })

        const cityName = computed(() => {
            if (props.city) return props.city.name
            return null
        })

        const bbox = computed(() => {
            if (props.city) {
                if (props.city.coordinates) return props.city.coordinates
            }

            return null
        })

        return {
            state,
            onMapReady,
            onMapLayerChange,
            isYandex,
            isTwoGis,
            onSelectAddress,
            cityName,
            bbox,
        }
    },
})
